import {Injectable} from '@angular/core';

import {loadModules} from 'esri-loader';

import {MapService} from '../map/map.service';
import {GoogleAnalyticsService} from '../googleAnalytics/google-analytics.service';
import esri = __esri;

/**
 * Service to create/control esri graphics layers
 * Also creates simple shapes when needed
 */
@Injectable({
  providedIn: 'root'
})
export class GraphicsService {
  private graphicsAr: Array<esri.GraphicsLayer> = [];

  constructor(private map: MapService, private ga: GoogleAnalyticsService) { }

  /**
   * Creates and adds a graphics layer to the esri map object
   * @param type: string name for the layer
   */
  public async addGraphicsLayer(type) {
    try {
      if (this.map.getMapObject()) {
        const [GraphicsLayer] = await loadModules([
          'esri/layers/GraphicsLayer'
        ]);
        const layer = await new GraphicsLayer({
          title: type
        });
        this.graphicsAr.push(layer);
        await this.map.getMapObject().add(layer);
        return layer;
      }
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

  public getGraphicsAr() {
    return this.graphicsAr;
  }

  /**
   * Checks to see if the map layer exists, and if it does removes it from the esri map object
   * @param layer: layer object to remove
   */
  public async removeGraphicsLayer(layer) {
    if (this.graphicsAr[this.graphicsAr.indexOf(layer)]) {
      this.graphicsAr.slice(this.graphicsAr.indexOf(layer), 1);
      await this.map.getMapObject().remove(layer);
    }
  }

  /**
   *  public function to create new graphics of any type
   */
  public async createGraphics(geometryType: string, layer: esri.GraphicsLayer, geometry: any, style: any) {
    if (layer) {
      if (geometryType === 'esriGeometryPoint' || geometryType === 'point') {
        return await this.createPoint(geometry, style.point, layer);
      } else if (geometryType === 'esriGeometryPolyline' || geometryType === 'polyline') {
        return await this.createPolyline(geometry, style.polyline, layer);
      } else if (geometryType === 'esriGeometryPolygon' || geometryType === 'polygon') {
        return await this.createPolygon(geometry.rings, style.polygon, layer);
      }
    }
  }

  /**
   * Creates a point graphic and adds it to a specific layer
   * @param geometry: esri geometry object
   * @param style: esri style object
   * @param layer: esri graphics layer
   */
  public async createPoint(geometry, style, layer) {
    try {
      const [Point, Graphic, webMercatorUtils, SimpleMarkerSymbol] = await loadModules([
        'esri/geometry/Point',
        'esri/Graphic',
        'esri/geometry/support/webMercatorUtils',
        'esri/symbols/SimpleMarkerSymbol'
      ]);
      if (geometry && geometry.x) {

        const point = new Point({
          longitude: geometry.x,
          latitude: geometry.y
        });
        let pointGraphic = new Graphic({
          geometry: webMercatorUtils.webMercatorToGeographic(point),
          symbol: new SimpleMarkerSymbol(style)
        });
        if (geometry.wkid && geometry.wkid == 4326 ) {
            pointGraphic = new Graphic({
            geometry: point,
            symbol: new SimpleMarkerSymbol(style)
          });
        }

        layer.add(pointGraphic);

        return pointGraphic;
      } else {
        console.log('Bad geometry');
      }
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

  /**
   * Creates a polyline graphic and adds it to a specific layer
   * @param geometry: esri geometry object
   * @param style: esri style object
   * @param layer: esri graphics layer
   */
  public async createPolyline(geometry, style, layer) {
    try {
      const [esriPolyline, Graphic, webMercatorUtils, esriSimpleLineSymbol] = await loadModules([
        'esri/geometry/Polyline',
        'esri/Graphic',
        'esri/geometry/support/webMercatorUtils',
        'esri/symbols/SimpleLineSymbol'
      ]);
      if (geometry && geometry.paths) {
        const polyline = new esriPolyline({
          paths: geometry.paths
        });
        const polylineGraphic = new Graphic({
          geometry: webMercatorUtils.webMercatorToGeographic(polyline),
          symbol: new esriSimpleLineSymbol(style)
        });
        layer.add(polylineGraphic);

        return polylineGraphic;
      } else {
        console.log('Bad geometry');
      }
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

  public async polygonFromExtent(extent: esri.Extent): Promise<esri.Polygon> {
    const [Polygon] = await loadModules([
      'esri/geometry/Polygon'
    ]);
    return Polygon.fromExtent(extent);
  }

  /**
   * Creates a polygon graphic and adds it to a specific layer
   * @param geometry: esri geometry object
   * @param style: esri style object
   * @param layer: esri graphics layer
   */
  public async createPolygon(geometry, style, layer) {
    try {
      const [Polygon, Graphic, webMercatorUtils, SimpleFillSymbol] = await loadModules([
        'esri/geometry/Polygon',
        'esri/Graphic',
        'esri/geometry/support/webMercatorUtils',
        'esri/symbols/SimpleFillSymbol'
      ]);
      if (geometry) {
        const polygon = new Polygon({
          rings: geometry
        });
        let polygonGraphic: any;
        polygonGraphic = new Graphic({
          geometry: webMercatorUtils.webMercatorToGeographic(polygon),
          symbol: new SimpleFillSymbol(style)
        });

        layer.add(polygonGraphic);
        return polygonGraphic;
      }
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

  public zoomToGraphicsLayer(layer, zoom?) {
    if (zoom && zoom > 0) {
      this.map.getViewRef().goTo(layer,zoom);
    }
    else {
      this.map.getViewRef().goTo(layer);
    }
  }
}
